import type { ResolveInstrumentResponse } from "../types/instruments";
import type {
  BrokerAssetClassHints,
  InstrumentSetupResolvedRow,
  InstrumentSetupRow,
} from "../types/instrumentSetup";
import type { MarketDataSourceOption } from "../types/marketDataSources";
import {
  effectiveMarketDataAttributionForSetupRow,
  formatMarketDataSourceShortLabel,
} from "./marketDataAttributionCopy";
import {
  humanizeMarketDataBrokerSlug,
  stripInternalMarketDataLabelSuffix,
} from "./marketDataSourceChoice";

/**
 * Prefer an explicit class from the row/hints. Never invent one from
 * `allowed_asset_classes[0]` — that forced commodity onto crypto symbols on resolve.
 */
export function resolveInitialAssetClassForSetupRow(
  row: Pick<InstrumentSetupRow, "asset_class" | "suggested_asset_class">,
  hints?: BrokerAssetClassHints
): string {
  const fixed = hints?.fixed_values?.[0]?.trim().toLowerCase();
  if (fixed) return fixed;
  const suggested = row.suggested_asset_class?.trim().toLowerCase();
  if (suggested) return suggested;
  const known = row.asset_class?.trim().toLowerCase();
  if (known) return known;
  return "";
}

function marketDataSourcesRevisionSignature(
  sources: MarketDataSourceOption[] | null | undefined
): string {
  return (sources ?? [])
    .map((source) =>
      [
        source.broker ?? source.market_data_broker ?? "",
        source.md_listing_key ?? "",
        source.available === false ? "0" : "1",
        source.is_selected ? "1" : "0",
      ].join(":")
    )
    .join("|");
}

/** Stable fingerprint for row market-data state — bumps when availability/selection changes. */
export function instrumentSetupRowDataRevision(
  row: Pick<
    InstrumentSetupRow,
    | "saved_market_data_broker"
    | "market_data_broker"
    | "md_listing_key"
    | "market_data_sources"
  >
): string {
  return [
    row.saved_market_data_broker ?? "",
    row.market_data_broker ?? "",
    row.md_listing_key ?? "",
    marketDataSourcesRevisionSignature(row.market_data_sources),
  ].join("::");
}

/** User-facing listing summary for symbol pickers — never raw md_listing_key or broker slugs. */
export function formatInstrumentSetupListingSummary(
  row: InstrumentSetupResolvedRow | InstrumentSetupRow
): string {
  const fromAttribution = formatMarketDataSourceShortLabel({
    attribution: effectiveMarketDataAttributionForSetupRow(row),
    sourceLabel: row.market_data_source_label,
    marketDataBrokerDisplay: row.market_data_broker_display,
  });
  if (fromAttribution && fromAttribution !== "Chart OHLC source") {
    return fromAttribution;
  }

  const sourceLabel = row.market_data_source_label?.trim() || "";
  const strippedLabel = stripInternalMarketDataLabelSuffix(sourceLabel);
  if (strippedLabel) return strippedLabel;

  const brokerDisplay = row.market_data_broker_display?.trim();
  if (brokerDisplay) return `OHLC via ${brokerDisplay}`;

  const listing = row.md_listing_key?.trim() || "";
  const brokerSlug = row.market_data_broker?.trim() || "";
  const broker =
    brokerDisplay || humanizeMarketDataBrokerSlug(brokerSlug) || brokerSlug;
  if (listing && broker) return `${broker} · ${listing}`;
  if (listing) return listing;
  if (broker) return `OHLC via ${broker}`;
  return "Not configured";
}

/**
 * Build a wizard-compatible row from instrument-setup `resolved[]`.
 * BE may return the full resolve shape on each resolved entry — pass it through.
 */
export function instrumentSetupRowFromResolvedSummary(
  resolved: InstrumentSetupResolvedRow
): InstrumentSetupRow {
  const symbol = resolved.symbol.trim();
  const hasAssetClass = Boolean(resolved.asset_class?.trim());
  const configuredLane =
    Boolean(resolved.saved_market_data_broker?.trim()) ||
    (Boolean(resolved.market_data_broker?.trim()) && Boolean(resolved.md_listing_key?.trim()));

  return {
    ...resolved,
    symbol,
    trade_count: resolved.trade_count ?? 0,
    status: "resolved",
    saved_market_data_broker:
      resolved.saved_market_data_broker?.trim() ||
      (configuredLane ? resolved.market_data_broker?.trim() || null : null),
    requires_market_data_source_choice:
      resolved.requires_market_data_source_choice ?? !configuredLane,
    requires_asset_class_confirmation:
      resolved.requires_asset_class_confirmation ?? !hasAssetClass,
  };
}

/**
 * Overlay a resolve response onto a setup row so the editor mounts with the
 * real asset class / sources instead of empty defaults.
 */
export function mergeInstrumentSetupRowWithResolve(
  row: InstrumentSetupRow,
  resolve: ResolveInstrumentResponse
): InstrumentSetupRow {
  return {
    ...row,
    asset_class: resolve.asset_class ?? row.asset_class,
    suggested_asset_class: resolve.suggested_asset_class ?? row.suggested_asset_class,
    valid_asset_classes: resolve.valid_asset_classes ?? row.valid_asset_classes,
    market_data_sources: resolve.market_data_sources ?? row.market_data_sources,
    candidate_brokers: resolve.candidate_brokers ?? row.candidate_brokers,
    selected_market_data_source:
      resolve.selected_market_data_source ?? row.selected_market_data_source,
    md_listing_key: resolve.md_listing_key ?? row.md_listing_key,
    instrument_id: resolve.instrument_id ?? row.instrument_id,
    market_data_broker: resolve.market_data_broker ?? row.market_data_broker,
    market_data_broker_display:
      resolve.market_data_broker_display ?? row.market_data_broker_display,
    market_data_source_label:
      resolve.market_data_source_label ?? row.market_data_source_label,
    market_data_attribution:
      resolve.market_data_attribution ?? row.market_data_attribution,
    requires_market_data_source_choice:
      resolve.requires_market_data_source_choice ??
      row.requires_market_data_source_choice ??
      true,
    requires_quote_variant_choice:
      resolve.requires_quote_variant_choice ?? row.requires_quote_variant_choice,
    quote_variant_overridable:
      resolve.quote_variant_overridable ?? row.quote_variant_overridable,
    auto_resolved_quote_variant:
      resolve.auto_resolved_quote_variant ?? row.auto_resolved_quote_variant,
    resolved: resolve.resolved ?? row.resolved,
    requires_asset_class_confirmation:
      resolve.requires_asset_class_confirmation ??
      row.requires_asset_class_confirmation ??
      !Boolean((resolve.asset_class ?? row.asset_class)?.trim()),
    requires_market_data_consent:
      resolve.requires_market_data_consent ?? row.requires_market_data_consent,
    quote_variant_candidates:
      resolve.quote_variant_candidates ?? row.quote_variant_candidates,
    suggested_quote_variant:
      resolve.suggested_quote_variant ?? row.suggested_quote_variant,
  };
}
